import { Input, Select } from 'antd';
import type { HubSpotContactProperty, HubSpotDerivedSource } from './types';

// Pieces `HubSpotMappingForm.tsx` and Task 9's `TemplateImport.tsx` /
// `templateImport.ts` both need, pulled out to a leaf module neither of
// those two files imports back into. `HubSpotMappingForm.tsx` re-exports all
// of these (so `ConnectionDetail.tsx` and anything else already importing
// them from `./HubSpotMappingForm` keeps working unchanged) — this file
// exists ONLY to break what would otherwise be a
// `HubSpotMappingForm.tsx` <-> `TemplateImport.tsx`/`templateImport.ts`
// circular import: `TemplateImport.tsx` renders `<PropertyPicker>` and
// `HubSpotMappingForm.tsx` renders `<TemplateImport>`, so both directions
// are load-bearing and can't just be dropped. Under ts-jest's CommonJS
// output, `export function Foo` gets hoisted to an early `exports.Foo = Foo`
// assignment (function declarations are hoisted, so the value already
// exists), but `export const` bindings are assigned at their own source
// position — the two-hop cycle left one side reading the OTHER's `exports`
// object before it had a value to hoist, and `<TemplateImport
// properties={...} onApply={...} />` rendered as `undefined`
// ("Element type is invalid") in every `HubSpotMappingForm` test, not just
// Task 9's. Both consumers now depend one level deeper instead of on each
// other.

/**
 * Never mapping targets — mirrors `RESERVED_PROPERTY_TARGETS` in
 * viq-backend/shared/src/crm/propertyMap.ts (the adapter-owned properties
 * plus the six natively-synced canonical fields). Keep this set in sync with
 * that file by hand: a client-side miss here isn't a data-loss risk (the
 * backend's own `PropertyMapSchema` still rejects it at PUT time), it just
 * means the operator finds out from a 422 instead of inline.
 */
export const RESERVED_PROPERTY_TARGETS = new Set([
  'vendoiq_person_id',
  'hubspot_owner_id',
  'hs_additional_emails',
  'firstname',
  'lastname',
  'company',
  'jobtitle',
  'email',
  'phone',
]);

/**
 * Fixed derived-source vocabulary + display labels (spec §5.2). Exported so
 * `ConnectionDetail`'s read view renders the SAME friendly label the form's
 * own Source select shows, instead of the raw wire enum value.
 */
export const DERIVED_SOURCE_OPTIONS: Array<{ value: HubSpotDerivedSource; label: string }> = [
  { value: 'conference_name', label: 'Conference name' },
  { value: 'temperature', label: 'Temperature (Hot/Warm/Cold)' },
];

export function derivedSourceLabel(source: HubSpotDerivedSource): string {
  return DERIVED_SOURCE_OPTIONS.find((o) => o.value === source)?.label ?? source;
}

export interface PropertyPickerProps {
  properties: HubSpotContactProperty[] | null;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  allowClear?: boolean;
  /** Forwarded from the wrapping `Form.Item`'s auto-generated id — WITHOUT
   * this, antd's `<label for>` points at an id this component never renders,
   * and `getByLabelText` (the house testing idiom, see
   * SalesforceConnectionForm.test.tsx) can't find the control. */
  id?: string;
  /**
   * Accessible name for a usage with NO wrapping `Form.Item` (Task 9's
   * `TemplateImport` review table: plain `Table` cells, not `Form.Item`s, so
   * there's no auto-generated `<label for>` to rely on). Forwarded as
   * `aria-label`, letting `getByLabelText` find the control the same way it
   * does inside this form's own `Form.Item`-wrapped usages — pass one or the
   * other, not both, in practice.
   */
  ariaLabel?: string;
}

/** Shared picker for every property-name field in `HubSpotMappingForm` AND
 * Task 9's `TemplateImport` review table: a searchable Select over the
 * loaded properties (label + name both shown and searched), or a free-text
 * Input when the properties list failed to load. */
export function PropertyPicker({
  properties,
  value,
  onChange,
  placeholder,
  allowClear,
  id,
  ariaLabel,
}: PropertyPickerProps) {
  if (properties === null) {
    return (
      <Input
        id={id}
        aria-label={ariaLabel}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
      />
    );
  }
  return (
    <Select
      id={id}
      aria-label={ariaLabel}
      showSearch
      allowClear={allowClear}
      value={value}
      onChange={(v) => onChange?.(v)}
      placeholder={placeholder}
      style={{ minWidth: 260 }}
      options={properties.map((p) => ({ value: p.name, label: `${p.label} (${p.name})` }))}
      optionFilterProp="label"
    />
  );
}
